import type React from 'react'
import { useEffect } from 'react'

import { useUnit } from 'effector-react'
import { Transforms } from 'slate'

import { Error, useErrorReset } from '@/components/ui/Error'
import { Dialog } from '@/components/ui/dialog.tsx'
import { $openedDialogs } from '@/models/dialogs.ts'
import { resetSelectedMedia } from '@/models/vault/medias'
import { MessageEditor } from '@/modules/message/components/editor/message-editor'
import { MessageEditorForm } from '@/modules/message/components/editor/message-editor-form'

import { useMessageMediaQuery } from '../../query/message-media-query-context'
import { useMessageEditor } from './message-editor-context'
import { MessageActionButtons } from './messageActionButtons'

export interface MessageEditorDialogProps {
  title: React.ReactNode
  description: React.ReactNode
  onClose: () => void
  isOpen: boolean
  isEditing: boolean
  isSubmitting?: boolean
  submitError?: unknown
}

export const MessageEditorDialog = (props: MessageEditorDialogProps) => {
  const {
    isOpen,
    onClose,
    title,
    submitError,
    description,
    isSubmitting,
    isEditing,
  } = props
  const openedDialogs = useUnit($openedDialogs)
  const [errorMessage, resetErrorMessage] = useErrorReset(submitError)
  const media = useMessageMediaQuery()
  const { isSubmitLoading, editor } = useMessageEditor()

  useEffect(
    () => {
      if (!isOpen) {
        resetErrorMessage()
        resetSelectedMedia()

        // Fixes an unexpected application error when you press ESC
        // See https://github.com/ianstormtaylor/slate/pull/5571#issuecomment-2023034842
        Transforms.select(editor, {
          anchor: { path: [0, 0], offset: 0 },
          focus: { path: [0, 0], offset: 0 },
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen, resetErrorMessage],
  )

  useEffect(
    () => {
      if (isOpen) {
        media.refetch()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isOpen],
  )

  return (
    <Dialog
      panelClassName="sm:mx-8 sm:max-w-[800px]"
      show={isOpen}
      onClose={
        // hack to prevent closing the dialog when another dialog is opened
        openedDialogs.length ? undefined : onClose
      }
    >
      <Dialog.Header>
        <Dialog.Title className="text-center text-[30px]">
          {isEditing ? 'Edit Message' : title}
        </Dialog.Title>
        <Dialog.Description className="mx-auto max-w-[600px] text-center text-sm">
          {description}
        </Dialog.Description>
      </Dialog.Header>
      <MessageEditorForm>
        <MessageEditor />
        {submitError ? (
          <Error
            className="mt-4"
            error={errorMessage}
          />
        ) : null}
        <MessageActionButtons
          isLoading={isSubmitting || isSubmitLoading}
          onClose={onClose}
        />
      </MessageEditorForm>
    </Dialog>
  )
}
